// Shopify serves an unversioned relative module import from a long-lived CDN
// cache after a live theme update. The section supplies the current, versioned
// core URL; local/unit-test imports safely use the relative fallback.
const configuredCoreUrl = typeof document === 'undefined'
  ? null
  : document.querySelector('arista-bracelet-pair[data-core-url]')?.dataset.coreUrl;
const fallbackCoreUrl = new URL('./arista-bracelet-pair-core.js', import.meta.url).href;
const coreUrl = typeof configuredCoreUrl === 'string' && configuredCoreUrl.length > 0
  ? configuredCoreUrl
  : fallbackCoreUrl;
const { PairCore, CartRequestError, STATUS_COMPLETED, STATUS_REJECTED, reconcileCartItems, normalizeInitial } = await import(coreUrl);

class PersonalisedPair extends HTMLElement {
  connectedCallback() {
    if (this.started) {
      this.bindPresentationControls();
      this.bindCartFreshness();
      if (this.boundStorageListener) window.addEventListener('storage', this.boundStorageListener);
      this.observeSecondField();
      if (this.core) {
        this.core = this.makeCore();
        if (this.core.hasStartupFailure()) return this.showUnreadableState();
        this.refreshCompletedCart();
      }
      return;
    }
    this.started = true;
    this.form = this.querySelector('[data-pair-form]');
    this.submitButtons = [...this.querySelectorAll('[data-pair-submit]')];
    this.submitButton = this.submitButtons[0];
    this.checkButton = this.querySelector('[data-pair-reconcile]');
    this.newButton = this.querySelector('[data-pair-new]');
    this.savedNotice = this.querySelector('[data-pair-saved-notice]');
    this.savedMessage = this.querySelector('[data-pair-saved-message]');
    this.cartLinks = [...this.querySelectorAll('[data-pair-cart-link]')];
    this.status = this.querySelector('[data-pair-status]');
    this.summary = this.querySelector('[data-pair-summary]');
    this.summaryLabel = this.querySelector('.arista-personalised-pair__review-label');
    this.sticky = this.querySelector('[data-pair-sticky]');
    this.stickySummary = this.querySelector('[data-pair-sticky-summary]');
    this.recovery = this.querySelector('[data-pair-recovery]');
    this.recoveryMessage = this.querySelector('[data-pair-recovery-message]');
    this.recoveryActions = this.querySelector('[data-pair-recovery-actions]');
    this.recoveryReset = this.querySelector('[data-pair-recovery-reset]');
    this.fields = [...this.querySelectorAll('[data-slot]')];
    this.secondField = this.fields.find(field => field.dataset.slot === '2');
    this.secondSlotInEditingViewport = false;
    this.ids = [...this.querySelectorAll('[data-variant-choice]')]
      .map(choice => choice.dataset.variantValue)
      .filter(Boolean);
    this.key = this.dataset.storageKey;
    if (!this.form) return;
    this.bindPresentationControls();
    if (!this.hasOperationLocks()) {
      this.showLockUnavailableState();
      return;
    }
    try {
      this.storage = window.localStorage;
      this.checkStorageReadiness();
      this.core = this.makeCore();
    } catch {
      // Storage failure is not a corrupt saved-record recovery. Do not expose
      // an unbound or misleading clear-state action; cart/contact stay usable.
      if (this.recoveryReset) this.recoveryReset.hidden = true;
      this.showRecovery('This browser cannot save your selection safely. Enable website storage or open this page in another current browser. If you already added a pair, check your cart first.', { showActions: true });
      return;
    }
    if (this.core.hasStartupFailure()) {
      this.showUnreadableState();
      return;
    }
    this.bindBuilderControls();
    this.showBuilder();
    this.observeSecondField();
    this.render();
    this.bindCartFreshness();
    this.refreshCompletedCart();
  }

  bindCartFreshness() {
    if (this.boundPageShow) return;
    this.boundPageShow = event => {
      // Initial loads are checked by connectedCallback; only BFCache restores
      // need a second lifecycle check.
      if (!event.persisted || this.busy || !this.core) return;
      this.core = this.makeCore();
      if (this.core.hasStartupFailure()) return this.showUnreadableState();
      this.refreshCompletedCart();
    };
    window.addEventListener('pageshow', this.boundPageShow);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (!this.busy && this.core?.getStatus() === STATUS_COMPLETED) this.refreshCompletedCart();
      });
    }
  }

  async refreshCompletedCart() {
    const generation = this.cartFreshnessGeneration = (this.cartFreshnessGeneration || 0) + 1;
    this.completedCartState = 'unverified';
    if (this.core.getStatus() !== STATUS_COMPLETED) return this.render();
    const core = this.core;
    const record = core.getActiveRecord();
    let saved;
    try { saved = this.storage.getItem(this.key); } catch { return this.render(); }
    this.completedCartState = 'pending';
    this.render();
    let state = 'unverified';
    try {
      const response = await this.fetchWithTimeout(this.dataset.cartUrl + '.js', {
        method: 'GET', cache: 'no-store', headers: { Accept: 'application/json' },
      }, 15000);
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      // The matcher accepts missing data as empty; validate transport data first.
      if (!cart || !Array.isArray(cart.items) || cart.items.some(item =>
        !item || typeof item !== 'object' || Array.isArray(item) ||
        !Number.isInteger(item.quantity) || item.quantity < 1 ||
        (typeof (item.variant_id ?? item.id) !== 'number' && typeof (item.variant_id ?? item.id) !== 'string') ||
        (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))
      )) throw new Error('Invalid cart');
      const result = reconcileCartItems(cart, record.pairId, record.slots[1], record.slots[2]);
      state = result.complete ? 'present' : result.reason === 'empty' ? 'absent' : 'partial';
    } catch { /* A failed read never changes the saved operation or cart. */ }
    if (generation !== this.cartFreshnessGeneration || this.core !== core || this.isConnected === false) return;
    try {
      if (this.storage.getItem(this.key) !== saved) state = 'unverified';
    } catch { state = 'unverified'; }
    this.completedCartState = state;
    if (state === 'absent' && await this.resetAbsentCompletedPair(core, saved, generation)) {
      return this.render('Your previous pair is no longer in this cart. Choose a new pair below.');
    }
    this.render();
  }

  async resetAbsentCompletedPair(core, saved, generation) {
    if (this.busy || !this.hasOperationLocks()) return false;
    let cleared = false;
    try {
      await this.withOperationLock(async lock => {
        if (!lock || this.busy || this.isConnected === false ||
            generation !== this.cartFreshnessGeneration || this.core !== core ||
            this.completedCartState !== 'absent' || core.getStatus() !== STATUS_COMPLETED ||
            this.storage.getItem(this.key) !== saved) return;
        const persisted = this.makeCore();
        if (persisted.hasStartupFailure() || persisted.getStatus() !== STATUS_COMPLETED ||
            JSON.stringify(persisted.getActiveRecord()) !== JSON.stringify(core.getActiveRecord())) return;
        this.storage.removeItem(this.key);
        if (this.storage.getItem(this.key) !== null) return;
        this.core = this.makeCore();
        if (this.core.hasStartupFailure()) return;
        this.clearPairFields();
        this.completedCartState = 'unverified';
        cleared = true;
      });
    } catch { /* Keep the operation guarded if the browser cannot reset it safely. */ }
    return cleared;
  }

  clearPairFields() {
    this.form.reset();
    this.fields.forEach(field => {
      delete field.dataset.productFirstInvalid;
      field.querySelector('[data-variant]').value = '';
      field.querySelector('[data-initial]').value = '';
    });
  }

  completedCartMessage() {
    return ({
      pending: 'Checking whether your saved pair is still in your cart…',
      present: 'Your pair is in your cart. Continue to cart to review both pieces.',
      absent: 'Your saved pair is no longer in this cart. View your cart or choose another pair. Nothing has been added again.',
      partial: 'Your cart no longer matches both saved pieces. View your cart before choosing another pair. Nothing has been added again.',
      unverified: 'We could not confirm whether your saved pair is still in your cart. View your cart before choosing another pair.',
    })[this.completedCartState || 'unverified'];
  }

  hasOperationLocks() {
    return typeof globalThis.navigator?.locks?.request === 'function';
  }

  checkStorageReadiness() {
    const probeKey = `${this.key}:readiness:${this.newPairId()}`;
    const probeValue = 'ready';
    try {
      this.storage.setItem(probeKey, probeValue);
      if (this.storage.getItem(probeKey) !== probeValue) throw new Error('Storage unavailable');
    } finally {
      this.storage.removeItem(probeKey);
    }
  }

  bindBuilderControls() {
    if (this.builderBound) return;
    this.builderBound = true;
    this.form.addEventListener('click', event => {
      if (event.target.matches?.('[data-initial]') &&
          this.enforceProductFirst(event.target.closest?.('[data-slot]'), event)) return;
      this.handleChoice(event);
    });
    for (const type of ['pointerdown', 'beforeinput', 'paste', 'keydown']) {
      this.form.addEventListener(type, event => {
        if (!event.target.matches?.('[data-initial]')) return;
        if (type === 'keydown' && (event.key === 'Tab' || event.key === 'Escape' ||
            event.metaKey || event.ctrlKey || event.altKey || event.key?.startsWith('Arrow'))) return;
        this.enforceProductFirst(event.target.closest?.('[data-slot]'), event);
      });
    }
    this.form.addEventListener('input', event => {
      if (!event.target.matches?.('[data-initial]') || this.busy || this.core.isLocked() ||
          this.core.getStatus() === STATUS_COMPLETED) return;
      if (this.enforceProductFirst(event.target.closest?.('[data-slot]'), event)) return;
      this.dismissCelebration();
      this.render();
    });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.send(); });
    this.checkButton?.addEventListener('click', () => this.check());
    this.newButton?.addEventListener('click', () => this.newPair());
    this.boundStorageListener = event => {
      if (event.key === this.key && !this.busy) {
        this.core = this.makeCore();
        if (this.core.hasStartupFailure()) {
          this.showUnreadableState();
          return;
        }
        this.showBuilder();
        this.refreshCompletedCart();
      }
    };
    window.addEventListener('storage', this.boundStorageListener);
  }

  bindRecoveryControls() {
    if (this.recoveryBound) return;
    this.recoveryBound = true;
    this.recoveryReset?.addEventListener('click', () => this.clearStartupFailure());
  }

  disconnectedCallback() {
    this.cartFreshnessGeneration = (this.cartFreshnessGeneration || 0) + 1;
    if (this.boundPageShow) window.removeEventListener('pageshow', this.boundPageShow);
    this.boundPageShow = null;
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = null;
    this.dismissCelebration();
    if (this.boundPresentationClick) this.removeEventListener?.('click', this.boundPresentationClick);
    if (this.boundPresentationKey) this.removeEventListener?.('keydown', this.boundPresentationKey);
    this.presentationBound = false;
    this.secondFieldObserver?.disconnect();
    if (this.boundStorageListener) window.removeEventListener('storage', this.boundStorageListener);
  }

  showBuilder() {
    if (this.form) this.form.hidden = false;
    if (this.recovery) this.recovery.hidden = true;
    if (this.recoveryActions) this.recoveryActions.hidden = true;
  }

  showRecovery(message, { showActions = false } = {}) {
    this.dismissCelebration();
    if (this.form) this.form.hidden = true;
    if (this.sticky) this.sticky.hidden = true;
    this.submitButtonsList().forEach(button => { button.disabled = true; });
    if (this.recovery) {
      this.recovery.hidden = false;
      if (this.recoveryMessage) this.recoveryMessage.textContent = message;
      else this.recovery.textContent = message;
    }
    if (this.recoveryActions) this.recoveryActions.hidden = !showActions;
  }

  showUnreadableState() {
    this.bindRecoveryControls();
    if (this.recoveryReset) this.recoveryReset.hidden = false;
    this.showRecovery('We could not safely read a saved personalisation request. Check your cart first. If no pair is there, clear the saved browser state to start again.', { showActions: true });
  }

  showLockUnavailableState() {
    this.bindRecoveryControls();
    if (this.recoveryReset) this.recoveryReset.hidden = true;
    this.showRecovery('This browser cannot safely confirm a two-piece cart request. Check your cart first, then use a current browser to choose your pair.', { showActions: true });
  }

  async clearStartupFailure() {
    if (this.busy) return;
    if (!this.hasOperationLocks()) return this.showLockUnavailableState();
    this.busy = true;
    try {
      await this.withOperationLock(async lock => {
        if (!lock) {
          this.showRecovery('Another tab is updating this pair. Wait for it to finish, then try again.', { showActions: true });
          return;
        }
        this.checkStorageReadiness();
        this.core = this.makeCore();
        // A stale recovery screen must never erase another tab's newer request.
        const clearRequired = this.core.hasStartupFailure();
        if (clearRequired) {
          await Promise.resolve(this.storage?.removeItem(this.key));
          if (this.storage?.getItem(this.key) !== null) throw new Error('Saved state was not cleared');
          this.core = this.makeCore();
          if (this.core.hasStartupFailure()) throw new Error('Saved state remains unreadable');
        }
        this.bindBuilderControls();
        this.showBuilder();
        this.observeSecondField();
        this.busy = false;
        this.render(clearRequired ? 'Saved browser state cleared. Start with piece 1.' : undefined);
      });
    } catch {
      this.showRecovery('We could not clear the saved browser state. Please check your cart, then reload this page before choosing a pair.', { showActions: true });
    } finally {
      this.busy = false;
    }
  }

  observeSecondField() {
    this.secondFieldObserver?.disconnect();
    if (!this.secondField || typeof IntersectionObserver === 'undefined') {
      /* Fail closed: the full review CTA remains usable, but a fixed CTA never covers editing controls. */
      this.secondSlotInEditingViewport = true;
      return;
    }
    this.secondFieldObserver = new IntersectionObserver(entries => {
      this.secondSlotInEditingViewport = Boolean(entries.some(entry => entry.isIntersecting));
      this.render();
    }, { rootMargin: `0px 0px -${this.stickySafeArea()}px 0px`, threshold: 0.02 });
    this.secondFieldObserver.observe(this.secondField);
  }

  stickySafeArea() {
    const configured = globalThis.getComputedStyle?.(this)?.getPropertyValue('--arista-sticky-safe-area').trim();
    const parsed = Number.parseFloat(configured);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 92;
  }

  timeoutSignal(milliseconds) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return { signal: AbortSignal.timeout(milliseconds), cancel: () => {} };
    }
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), milliseconds);
      return { signal: controller.signal, cancel: () => clearTimeout(timer) };
    }
    return { signal: undefined, cancel: () => {} };
  }

  async fetchWithTimeout(url, options, milliseconds) {
    const timeout = this.timeoutSignal(milliseconds);
    try {
      return await fetch(url, { ...options, signal: timeout.signal });
    } finally {
      timeout.cancel();
    }
  }

  makeCore() {
    let knownVariantIds = this.ids;
    try {
      const known = JSON.parse(this.dataset.knownVariantIds || 'null');
      if (Array.isArray(known) && known.every(id => typeof id === 'string' || typeof id === 'number')) {
        knownVariantIds = [...new Set([...this.ids, ...known.map(String)])];
      }
    } catch { /* The new-choice allowlist remains the fail-closed fallback. */ }
    return new PairCore({
      storage: this.storage,
      storageKey: this.key,
      allowedVariantIds: this.ids,
      restorableVariantIds: knownVariantIds,
      transport: async payload => {
        const response = await this.fetchWithTimeout(this.dataset.addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        }, 20000);
        if (!response.ok) {
          let body;
          try { body = await response.json(); } catch { /* No body is not a known safe rejection. */ }
          const reason = typeof body?.description === 'string' ? body.description
            : typeof body?.message === 'string' ? body.message : '';
          if ([400, 422].includes(response.status) && reason) throw new CartRequestError(response.status, reason);
          throw new Error('Cart request not confirmed');
        }
        return response.json();
      },
    });
  }

  values() {
    return this.fields.map(field => ({
      variantId: field.querySelector('[data-variant]').value,
      initial: field.querySelector('[data-initial]').value,
    }));
  }

  isValidName(value) {
    try { normalizeInitial(value); return true; } catch { return false; }
  }

  isValidValue(value) {
    return Boolean(value && this.ids.includes(value.variantId) && this.isValidName(value.initial));
  }

  selectionIssue(values = this.values()) {
    if (values.length !== 2 || !values.every(value => this.isValidValue(value)) ||
        values[0].variantId !== values[1].variantId) return null;
    const choice = this.fields[0]?.querySelector(`[data-variant-choice][data-variant-value="${values[0].variantId}"]`);
    if (choice?.dataset.variantMaxQuantity === '1') {
      return 'Only one piece of this design is available. Choose a different design for one piece to complete your pair.';
    }
    return null;
  }

  choiceLabel(field, value) {
    if (!field || !value?.variantId || !value?.initial) return null;
    const choice = field.querySelector(`[data-variant-choice][data-variant-value="${value.variantId}"]`);
    let knownName;
    try { knownName = JSON.parse(this.dataset.knownVariantNames || '{}')[value.variantId]; } catch { /* Optional display name only. */ }
    const name = choice?.dataset.variantName || (typeof knownName === 'string' ? knownName : 'Previously selected design');
    const rawPrice = choice?.dataset.variantPrice;
    const price = rawPrice && /^\d+$/.test(rawPrice)
      ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(rawPrice) / 100)
      : null;
    return `${name} · ${value.initial}${price ? ' · ' + price : ''}`;
  }

  isProductSelected(field) {
    return Boolean(field && this.ids?.includes(String(field.querySelector('[data-variant]')?.value || '')));
  }

  enforceProductFirst(field, event) {
    if (!field || field.disabled || this.busy || this.core.isLocked() ||
        this.core.getStatus() === STATUS_COMPLETED || this.isProductSelected(field)) return false;
    event?.preventDefault?.();
    const initial = field.querySelector('[data-initial]');
    if (initial) initial.value = '';
    field.dataset.productFirstInvalid = 'true';
    this.dismissCelebration();
    this.render();
    const firstChoice = field.querySelector('[data-variant-choice]');
    field.scrollIntoView?.({ behavior: 'auto', block: 'start' });
    try { firstChoice?.focus?.({ preventScroll: true }); } catch { firstChoice?.focus?.(); }
    return true;
  }

  handleChoice(event) {
    const choice = event.target.closest('[data-variant-choice], [data-initial-choice]');
    if (!choice || choice.disabled || !this.contains(choice) || this.busy || this.core.isLocked() ||
        this.core.getStatus() === STATUS_COMPLETED) return;
    const field = choice.closest('[data-slot]');
    if (!field || field.disabled) return;
    if (choice.hasAttribute('data-initial-choice') && this.enforceProductFirst(field, event)) return;
    const previousValues = this.values();
    const firstWasReady = this.isValidValue(previousValues[0]);
    const pairWasReady = previousValues.length === 2 && previousValues.every(value => this.isValidValue(value)) && !this.selectionIssue(previousValues);
    this.dismissCelebration();
    const input = choice.hasAttribute('data-variant-choice')
      ? field.querySelector('[data-variant]')
      : field.querySelector('[data-initial]');
    input.value = choice.hasAttribute('data-variant-choice')
      ? choice.dataset.variantValue
      : choice.dataset.initialValue;
    this.render();
    const currentValues = this.values();
    const pairIsReady = currentValues.length === 2 && currentValues.every(value => this.isValidValue(value)) && !this.selectionIssue(currentValues);
    if (event.isTrusted === true && !pairWasReady && pairIsReady && this.dataset.approved === 'true') {
      this.lastCelebrationTrigger = choice;
      this.showCelebration();
    }
    if (!firstWasReady && this.isValidValue(this.values()[0])) {
      this.revealSecondField({ focus: event.detail === 0 });
    }
  }

  revealSecondField({ focus = false } = {}) {
    if (!this.secondField || this.secondField.hidden) return;
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    this.secondField.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    if (!focus) return;
    const firstChoice = this.secondField.querySelector('[data-variant-choice]');
    try {
      firstChoice?.focus({ preventScroll: true });
    } catch {
      firstChoice?.focus();
    }
  }

  bindPresentationControls() {
    if (this.presentationBound) return;
    this.presentationBound = true;
    this.boundPresentationClick = event => {
      const cartLink = event.target.closest?.('a[href]');
      if (cartLink && this.contains(cartLink) && !event.defaultPrevented && event.button === 0 &&
          !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
          !cartLink.hasAttribute('download') && (!cartLink.target || cartLink.target === '_self')) {
        const destination = new URL(cartLink.href, window.location.origin);
        const cartDestination = new URL(this.dataset.cartUrl, window.location.origin);
        if (destination.origin === cartDestination.origin && destination.pathname === cartDestination.pathname) {
          event.preventDefault();
          this.openNativeCart(cartLink);
          return;
        }
      }
      const close = event.target.closest?.('[data-pair-celebration-close]');
      if (close && this.contains(close)) {
        event.preventDefault();
        this.dismissCelebration();
        return;
      }
      const edit = event.target.closest?.('[data-pair-edit]');
      if (edit && this.contains(edit) && !edit.disabled) {
        event.preventDefault();
        this.editPiece(edit.dataset.pairEdit);
      }
    };
    this.boundPresentationKey = event => {
      if (event.key === 'Escape') this.dismissCelebration();
    };
    this.addEventListener?.('click', this.boundPresentationClick);
    this.addEventListener?.('keydown', this.boundPresentationKey);
  }

  editPiece(slot) {
    if (this.busy || this.core.isLocked() || this.core.getStatus() === STATUS_COMPLETED) return;
    const field = this.fields.find(candidate => candidate.dataset.slot === String(slot));
    if (!field || field.disabled || field.hidden) return;
    this.dismissCelebration();
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    field.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    const target = field.querySelector('[data-variant-choice][aria-pressed="true"]') || field.querySelector('[data-variant-choice]');
    try { target?.focus?.({ preventScroll: true }); } catch { target?.focus?.(); }
  }

  renderPresentation({ values, chosenCount, ready, locked, message }) {
    const meter = this.querySelector?.('[data-pair-meter]');
    if (meter?.style) meter.style.width = `${Math.min(2, Math.max(0, chosenCount)) * 50}%`;
    const count = this.querySelector?.('[data-pair-progress-count]');
    if (count) count.textContent = count.dataset[chosenCount === 2 ? 'ready' : chosenCount === 1 ? 'one' : 'empty'] || '';
    const activeStep = ready || (locked && chosenCount === 2) ? '3' : this.isValidValue(values[0]) ? '2' : '1';
    (this.querySelectorAll?.('[data-pair-step]') || []).forEach(step => {
      if (step.dataset.pairStep === activeStep) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
    (this.querySelectorAll?.('[data-pair-preview-slot]') || []).forEach(preview => {
      const index = Number(preview.dataset.pairPreviewSlot) - 1;
      const value = values[index];
      const field = this.fields[index];
      if (!value || !field) return;
      const choice = value.variantId ? field.querySelector(`[data-variant-choice][data-variant-value="${value.variantId}"]`) : null;
      let knownName;
      try { knownName = JSON.parse(this.dataset.knownVariantNames || '{}')[value.variantId]; } catch { /* Optional display-only fallback. */ }
      const name = choice?.dataset.variantName || (typeof knownName === 'string' ? knownName : preview.dataset.emptyLabel || '');
      const sourceImage = choice?.querySelector?.('img');
      const source = sourceImage?.currentSrc || sourceImage?.src;
      const image = preview.querySelector('[data-pair-preview-image]');
      if (image) {
        image.hidden = !source;
        if (source) {
          if (image.getAttribute?.('src') !== source) image.src = source;
          image.alt = sourceImage.alt || name;
        } else image.removeAttribute?.('src');
      }
      const nameNode = preview.querySelector('[data-pair-preview-name]');
      if (nameNode) nameNode.textContent = name;
      const initialNode = preview.querySelector('[data-pair-preview-initial]');
      const hasInitial = this.isValidName(value.initial);
      if (initialNode) {
        initialNode.textContent = hasInitial ? value.initial : '';
        initialNode.hidden = !hasInitial;
      }
      const detail = preview.querySelector('[data-pair-preview-detail]');
      if (detail) detail.textContent = hasInitial
        ? `${preview.dataset.initialLabel || ''} ${value.initial}`.trim()
        : preview.dataset.emptyLabel || '';
      const edit = preview.querySelector('[data-pair-edit]');
      if (edit) edit.disabled = Boolean(locked || !value.variantId);
      preview.dataset.complete = String(Boolean(value.variantId && hasInitial));
    });
    this.fields.forEach(field => {
      const selected = field.querySelector('[data-variant]').value;
      field.querySelectorAll('[data-variant-choice]').forEach(choice => {
        const label = choice.querySelector?.('[data-pair-choice-label]');
        if (label) label.textContent = label.dataset[choice.dataset.variantValue === selected ? 'selectedLabel' : 'defaultLabel'] || '';
      });
    });
    if (locked || message || !ready || this.core.getStatus() === STATUS_REJECTED || this.form?.hidden) this.dismissCelebration();
  }

  dismissCelebration() {
    clearTimeout(this.celebrationTimer);
    clearTimeout(this.confettiTimer);
    this.celebrationTimer = null;
    this.confettiTimer = null;
    const celebration = this.querySelector?.('[data-pair-celebration]');
    const focusWasInside = Boolean(celebration?.contains?.(this.ownerDocument?.activeElement));
    if (celebration) celebration.hidden = true;
    const confetti = this.querySelector?.('[data-pair-confetti]');
    confetti?.replaceChildren?.();
    if (confetti) confetti.hidden = true;
    if (focusWasInside) {
      const canFocus = candidate => {
        if (!candidate || typeof candidate.focus !== 'function' || candidate.disabled ||
            candidate.hidden || candidate.isConnected === false) return false;
        if (candidate.closest?.('fieldset')?.disabled || candidate.closest?.('[hidden]')?.hidden) return false;
        return typeof candidate.getClientRects !== 'function' || candidate.getClientRects().length > 0;
      };
      const target = [this.lastCelebrationTrigger, ...this.submitButtonsList(), ...(this.cartLinks || []), this.checkButton, this.newButton].find(canFocus);
      try { target?.focus({ preventScroll: true }); } catch { target?.focus(); }
    }
  }

  showCelebration() {
    if (this.busy || this.core.isLocked() || this.core.getStatus() === STATUS_COMPLETED ||
        this.core.getStatus() === STATUS_REJECTED || this.form?.hidden) return;
    const values = this.values();
    if (this.dataset.approved !== 'true' || values.length !== 2 ||
        !values.every(value => this.isValidValue(value)) || this.selectionIssue(values)) return;
    const celebration = this.querySelector?.('[data-pair-celebration]');
    if (!celebration) return;
    this.dismissCelebration();
    celebration.hidden = false;
    // Presentation only: no focus trap, cart action, event tracking or network call.
    this.celebrationTimer = setTimeout(() => this.dismissCelebration(), 6500);
    const confetti = this.querySelector?.('[data-pair-confetti]');
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!confetti || reducedMotion || !this.ownerDocument?.createElement) return;
    confetti.hidden = false;
    for (let index = 0; index < 24; index++) {
      const particle = this.ownerDocument.createElement('i');
      particle.className = 'arista-personalised-pair__confetti-piece';
      particle.setAttribute('aria-hidden', 'true');
      particle.style.setProperty('--confetti-x', `${(index * 37) % 100}%`);
      particle.style.setProperty('--confetti-delay', `${(index % 6) * 35}ms`);
      particle.style.setProperty('--confetti-rotation', `${index * 47}deg`);
      particle.style.setProperty('--confetti-color-index', String(index % 3));
      particle.dataset.color = String(index % 3);
      confetti.appendChild(particle);
    }
    this.confettiTimer = setTimeout(() => {
      confetti.replaceChildren();
      confetti.hidden = true;
      this.confettiTimer = null;
    }, 2300);
  }

  async withOperationLock(callback) {
    const locks = globalThis.navigator?.locks;
    if (locks && typeof locks.request === 'function') {
      return locks.request(this.key, { ifAvailable: true }, callback);
    }
    // Local-storage compare-and-set is not atomic across tabs. Shopify cart
    // adds are not idempotent by pair token, so a non-authoritative fallback
    // could duplicate a customer order. Current iOS/Android/desktop browsers
    // support Web Locks; unsupported browsers receive a clear recovery route.
    throw new Error('Browser does not support safe operation locks');
  }

  submitButtonsList() {
    return (this.submitButtons?.length ? this.submitButtons : [this.submitButton]).filter(Boolean);
  }

  render(message) {
    // Restored operations must display their own pieces, not blank controls or
    // another tab's stale selection. Unsaved edits to an idle/rejected pair stay local.
    if (this.core.isLocked() || this.core.getStatus() === STATUS_COMPLETED ||
        (this.core.getStatus() === STATUS_REJECTED && this.renderedCore !== this.core)) {
      const restored = this.core.getSlots();
      this.fields.forEach(field => {
        const slot = restored[field.dataset.slot];
        if (!slot) return;
        field.querySelector('[data-variant]').value = String(slot.variantId);
        field.querySelector('[data-initial]').value = slot.initial;
      });
    }
    this.renderedCore = this.core;
    const values = this.values();
    const complete = this.core.getStatus() === STATUS_COMPLETED;
    const selectionIssue = this.selectionIssue(values);
    const ready = values.length === 2 && values.every(value => this.isValidValue(value)) && !selectionIssue;
    const locked = this.busy || this.core.isLocked() || complete;
    const chosenCount = (complete || this.core.isLocked())
      ? Object.values(this.core.getSlots()).filter(value => value?.variantId && this.isValidName(value.initial)).length
      : values.filter(value => this.isValidValue(value)).length;
    this.fields.forEach(field => {
      field.disabled = locked;
      const productSelected = this.isProductSelected(field);
      if (productSelected) delete field.dataset.productFirstInvalid;
      const productError = field.querySelector('[data-product-first-error]');
      if (productError) productError.hidden = productSelected || field.dataset.productFirstInvalid !== 'true';
      const nameInput = field.querySelector('[data-initial]');
      if (nameInput?.type === 'text') {
        nameInput.readOnly = !productSelected;
        nameInput.placeholder = productSelected ? nameInput.dataset.readyPlaceholder : nameInput.dataset.productFirstPlaceholder;
      }
      const selected = {
        variantId: field.querySelector('[data-variant]').value,
        initial: field.querySelector('[data-initial]').value,
      };
      field.querySelectorAll('[data-variant-choice]').forEach(choice => {
        choice.setAttribute('aria-pressed', String(choice.dataset.variantValue === selected.variantId));
      });
      field.querySelectorAll('[data-initial-choice]').forEach(choice => {
        choice.setAttribute('aria-pressed', String(choice.dataset.initialValue === selected.initial));
      });
    });
    const secondField = this.secondField;
    if (secondField) secondField.hidden = !(locked || complete || this.isValidValue(values[0]));

    const primaryText = complete ? (this.completedCartState === 'present' ? 'Pair added to cart' : 'Saved pair') : selectionIssue ? 'Choose another design for one piece' : ready ? 'Add 2 personalised bracelets' : 'Complete both pieces to continue';
    this.submitButtonsList().forEach(button => {
      button.disabled = this.dataset.approved !== 'true' || locked || !ready;
      button.textContent = primaryText;
    });
    if (this.sticky) this.sticky.hidden = !(ready && !locked && this.dataset.approved === 'true' && !this.secondSlotInEditingViewport);
    if (this.checkButton) {
      this.checkButton.hidden = !this.core.isLocked();
      this.checkButton.disabled = Boolean(this.busy);
    }
    if (this.newButton) {
      this.newButton.hidden = !complete;
      this.newButton.disabled = Boolean(this.busy);
    }
    (this.cartLinks || []).forEach(link => {
      link.hidden = !(complete || this.core.isLocked());
      link.textContent = complete && this.completedCartState === 'present' ? 'Continue to cart' : 'View cart';
    });

    const labels = values.map((value, index) => this.choiceLabel(this.fields[index], value));
    const summary = chosenCount === 2
      ? `Piece 1: ${labels[0]} · Piece 2: ${labels[1]}`
      : chosenCount === 1
      ? `Piece 1: ${labels[0]} · Now personalise piece 2.`
      : 'Choose a design and name for each piece.';
    if (this.summary) this.summary.textContent = summary;
    if (this.summaryLabel) this.summaryLabel.textContent = `Your pair: ${chosenCount} of 2 chosen`;
    if (this.stickySummary) this.stickySummary.textContent = ready ? 'Your pair is ready' : `Your pair: ${chosenCount} of 2 chosen`;

    if (this.status) {
      this.status.textContent = message || (
        complete ? this.completedCartMessage()
          : this.core.isLocked() ? this.core.getCartRejection()
            ? `Shopify could not confirm both pieces: ${this.core.getCartRejection().reason} Check your cart below before trying again.`
            : 'Your previous request is not yet confirmed. Check your cart below so the pair is not added twice.'
          : selectionIssue ? selectionIssue
          : this.core.getStatus() === STATUS_REJECTED ? `Shopify could not add that pair: ${this.core.getCartRejection()?.reason || 'Please review your selection.'} We checked: neither piece from this request is in your cart. You can change your selection or try again.`
          : this.dataset.approved !== 'true' ? 'This selection is being prepared.'
          : chosenCount === 0 ? 'Start with piece 1.'
          : chosenCount === 1 ? 'Piece 1 is ready. Now personalise piece 2.'
          : 'Your pair is ready to add to cart.'
      );
    }
    if (this.savedNotice) this.savedNotice.hidden = !(complete || this.core.isLocked());
    if (this.savedMessage) this.savedMessage.textContent = this.status?.textContent || '';
    this.renderPresentation({ values, chosenCount, ready, locked, message });
  }

  newPairId() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  }

  async send() {
    if (this.submitButton.disabled || this.busy || !this.form.reportValidity()) return;
    if (!this.hasOperationLocks()) return this.showLockUnavailableState();
    const values = this.values();
    const selectionIssue = this.selectionIssue(values);
    if (selectionIssue) {
      this.render(selectionIssue);
      return;
    }
    let message;
    let added = false;
    this.busy = true;
    this.render('Adding both personalised pieces…');
    try {
      await this.withOperationLock(async lock => {
        if (!lock) {
          message = 'This pair is being checked in another tab. Wait for that tab to finish, then try again here.';
          return;
        }
        this.core = this.makeCore();
        if (this.core.isLocked() || this.core.getStatus() === STATUS_COMPLETED) return;
        values.forEach((value, index) => this.core.setSlot(index + 1, value));
        await this.core.submit(this.newPairId());
        added = true;
        this.cartFreshnessGeneration = (this.cartFreshnessGeneration || 0) + 1;
        this.completedCartState = 'present';
      });
    } catch {
      if (!this.core.isLocked()) message = 'We could not safely add that pair. Please check your cart before trying again.';
      /* An ambiguous result stays locked. It is never retried automatically. */
    } finally {
      this.busy = false;
      this.render(message);
    }
    if (added && this.isConnected) await this.openConfirmedPairCart();
  }

  async openConfirmedPairCart() {
    const pairId = this.core.getActiveRecord()?.pairId;
    if (!pairId || this.core.getStatus() !== STATUS_COMPLETED || this.cartHandoffPairId === pairId) return;
    // Claim this confirmed handoff before awaiting: failures only navigate to cart.
    this.cartHandoffPairId = pairId;
    await this.openNativeCart();
  }

  async openNativeCart(returnFocusTarget) {
    if (this.nativeCartOpening) return;
    this.nativeCartOpening = true;
    try {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
        throw new Error('Cart drawer unavailable');
      }
      const required = drawer.getSectionsToRender();
      const ids = required.map(section => section.id);
      if (!ids.includes('cart-drawer') || !ids.includes('cart-icon-bubble')) throw new Error('Incomplete drawer sections');
      const url = new URL(this.dataset.cartUrl, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await this.fetchWithTimeout(url.pathname + url.search, {
        // /cart negotiates application/json as cart data, ignoring section rendering.
        method: 'GET', cache: 'no-store',
      }, 15000);
      if (!response.ok) throw new Error('Cart sections unavailable');
      const sections = await response.json();
      if (!sections || ids.some(id => typeof sections[id] !== 'string' || !sections[id].trim())) {
        throw new Error('Incomplete cart sections');
      }
      if (typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        for (const section of required) {
          const html = parser.parseFromString(sections[section.id], 'text/html');
          if (!html.querySelector(section.selector || '.shopify-section')) throw new Error('Invalid cart section');
        }
        const html = parser.parseFromString(sections['cart-drawer'], 'text/html');
        if (!html.querySelector('#CartDrawer .drawer__inner') || !html.querySelector('#CartDrawer-Overlay')) {
          throw new Error('Invalid cart drawer');
        }
      }
      drawer.setActiveElement?.(returnFocusTarget || document.querySelector('#cart-icon-bubble'));
      drawer.renderContents({ sections });
    } catch {
      window.location.assign(this.dataset.cartUrl);
    } finally {
      this.nativeCartOpening = false;
    }
  }

  async check() {
    if (this.busy) return;
    if (!this.hasOperationLocks()) return this.showLockUnavailableState();
    let message;
    this.busy = true;
    this.render('Checking your cart…');
    try {
      await this.withOperationLock(async lock => {
        if (!lock) {
          message = 'This pair is being checked in another tab. Wait for that tab to finish, then check again here.';
          return;
        }
        this.core = this.makeCore();
        if (!this.core.isLocked()) return;
        const response = await this.fetchWithTimeout(this.dataset.cartUrl + '.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }, 15000);
        if (!response.ok) throw new Error('Cart unavailable');
        const result = await this.core.reconcile(await response.json());
        if (result.ok && result.status === STATUS_COMPLETED) {
          this.cartFreshnessGeneration = (this.cartFreshnessGeneration || 0) + 1;
          this.completedCartState = 'present';
        }
      });
    } catch {
      message = 'We could not finish checking your cart. Please view your cart or try checking again. Your pair has not been sent again.';
      /* An empty or failed cart read leaves the unresolved operation locked. */
    } finally {
      this.busy = false;
      this.render(message);
    }
  }

  async newPair() {
    if (this.busy || this.core.getStatus() !== STATUS_COMPLETED) return;
    if (!this.hasOperationLocks()) return this.showLockUnavailableState();
    this.busy = true;
    let message;
    try {
      await this.withOperationLock(async lock => {
        if (!lock) {
          message = 'Another tab is updating this pair. Wait for it to finish, then choose another pair.';
          return;
        }
        this.core = this.makeCore();
        if (this.core.getStatus() !== STATUS_COMPLETED) return;
        this.storage.removeItem(this.key);
        if (this.storage.getItem(this.key) !== null) throw new Error('Could not start a fresh pair');
        this.core = this.makeCore();
        this.clearPairFields();
      });
    } catch {
      this.core = this.makeCore();
    } finally {
      this.busy = false;
      this.render(message);
    }
  }
}

if (!customElements.get('arista-bracelet-pair')) customElements.define('arista-bracelet-pair', PersonalisedPair);
